import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, Select, Spinner, Button } from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight, FiTrash2 } from 'react-icons/fi';
import xhr from 'xhr';
import Month from './month';
import SubjectsCantidadesPicker from './SubjectsCantidadesPicker';

/**
 * Un ítem del multi-programa dentro del modal de nuevo pago. Encapsula la
 * selección de programa concreto, la resolución de la matrícula activa del
 * estudiante y el picker correspondiente (cantidades para is_semester_based o
 * meses para anualizados). Emite su estado consolidado hacia el padre para
 * que al guardar se creen N pagos, uno por ítem.
 *
 * Props:
 *   index               — 0-based, para el header ("Programa 1", "Programa 2", ...)
 *   studentId
 *   programs            — lista completa; se filtra por currentProgramType
 *   currentProgramType  — objeto { id, type, has_graduation_fee, is_semester_based }
 *   concept             — 'other_payments' | 'subjects' | 'others'
 *   value               — {programId, enrollment, cantidadesState, monthState}
 *   onChange(next)      — merge con `value`
 *   onRemove()          — solo si canRemove
 *   canRemove
 *   isOpenInitial       — si arranca colapsado o expandido
 *   paymentDate         — fecha del pago, para preseleccionar semestre en Month
 */
export default function PaymentProgramItem({
  index,
  studentId,
  programs,
  currentProgramType,
  concept,
  value,
  onChange,
  onRemove,
  canRemove,
  isOpenInitial = true,
  paymentDate,
}) {
  const [open, setOpen] = useState(isOpenInitial);
  const [fetchingEnrollment, setFetchingEnrollment] = useState(false);
  const [subjectsCount, setSubjectsCount] = useState(0);

  const programId = value.programId ?? '';
  const currentProgram = programs.find(
    (el) => Number(el.id) === Number(programId) && el.program_type?.type === currentProgramType?.type
  );
  const isSemesterBased =
    currentProgram?.program_type?.is_semester_based === true ||
    currentProgramType?.is_semester_based === true;
  const hasGraduationFee =
    currentProgram?.program_type?.has_graduation_fee === true ||
    currentProgramType?.has_graduation_fee === true;

  // Cargar matrícula activa del estudiante para (programa, tipo).
  useEffect(() => {
    if (!currentProgram || !currentProgramType?.id || !studentId) {
      onChange({ enrollment: null });
      return;
    }
    let cancelled = false;
    (async () => {
      setFetchingEnrollment(true);
      try {
        const res = await xhr(
          `/${currentProgramType.type.replace('_', '-')}-enrollments?filters[student][id][$eq]=${studentId}` +
          `&filters[${currentProgramType.type}][id][$eq]=${currentProgram.id}` +
          `&filters[${currentProgramType.type}][program_type][id][$eq]=${currentProgramType.id}` +
          `&populate=${currentProgramType.type}&populate=semester&sort[0]=updatedAt:desc`
        );
        if (cancelled) return;
        const enr = res.data?.data?.[0] || null;
        onChange({ enrollment: enr || false });
      } catch (err) {
        console.error('resolve enrollment:', err);
        if (!cancelled) onChange({ enrollment: false });
      } finally {
        if (!cancelled) setFetchingEnrollment(false);
      }
    })();
    return () => { cancelled = true; };
  }, [currentProgram?.id, currentProgramType?.id, studentId]); // eslint-disable-line

  // Cargar total de materias del programa (para OrdinalsPicker fallback).
  useEffect(() => {
    if (!currentProgram || !currentProgramType?.type || currentProgramType.type === 'course') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await xhr(
          `/subjects?filters[${currentProgramType.type}][id][$eq]=${currentProgram.id}&pagination[limit]=-1&fields=id`
        );
        if (!cancelled) setSubjectsCount(res.data?.data?.length || 0);
      } catch (err) {
        console.error('load subjects count:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentProgram?.id, currentProgramType?.type]);

  const handleProgramChange = (e) => {
    const nextId = e.target.value ? Number(e.target.value) : null;
    onChange({
      programId: nextId,
      enrollment: null,
      cantidadesState: null,
      monthState: null,
    });
  };

  const currentEnrollment = value.enrollment;
  const showPickerSection =
    concept === 'other_payments' && currentProgramType?.type && currentProgramType.type !== 'course' && currentProgram;

  return (
    <Box
      border="1px solid"
      borderColor={open ? 'purple.300' : 'gray.200'}
      borderRadius="8px"
      overflow="hidden"
      marginBottom="8px"
      bg="white"
    >
      <Flex
        align="center"
        justify="space-between"
        padding="10px 14px"
        cursor="pointer"
        onClick={() => setOpen((v) => !v)}
        bg={open ? '#F5F3FF' : 'gray.50'}
        borderBottom={open ? '1px solid' : 'none'}
        borderColor={open ? 'purple.200' : 'transparent'}
      >
        <Flex align="center" gap={2}>
          <Box color={open ? '#5B21B6' : 'gray.500'}>
            {open ? <FiChevronDown /> : <FiChevronRight />}
          </Box>
          <Text fontSize="13px" fontWeight="600" color={open ? '#5B21B6' : 'gray.700'}>
            Programa {index + 1}
          </Text>
          {currentProgram && (
            <Text fontSize="12px" color="gray.600" ml={2}>
              · {currentProgram.name}
            </Text>
          )}
        </Flex>
        {canRemove && (
          <Button
            size="xs"
            variant="ghost"
            colorScheme="red"
            leftIcon={<FiTrash2 />}
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
          >
            Quitar
          </Button>
        )}
      </Flex>

      {open && (
        <Box padding="14px 16px">
          <Box marginY="5px" fontSize="13px" color="gray.700">Programa *</Box>
          <Select value={programId} onChange={handleProgramChange} size="sm">
            <option value="">Seleccione</option>
            {programs
              .filter((p) => p.program_type?.type === currentProgramType?.type)
              .map((el) => (
                <option key={el.id} value={el.id}>{el.name}</option>
              ))}
          </Select>

          {/* Programas por materias */}
          {showPickerSection && isSemesterBased && (
            fetchingEnrollment ? (
              <Flex justifyContent="center" paddingY="lg">
                <Spinner />
              </Flex>
            ) : currentEnrollment?.id ? (
              <SubjectsCantidadesPicker
                key={`scp:${currentProgramType.type}:${currentEnrollment.id}`}
                enrollmentType={currentProgramType.type}
                enrollment={currentEnrollment}
                hasGraduationFee={hasGraduationFee}
                onStateChange={(st) => onChange({ cantidadesState: st })}
              />
            ) : currentProgram && currentEnrollment === false ? (
              <Box padding="12px" color="gray.600" fontSize="13px" border="1px solid #e2e8f0" borderRadius="6px" marginY="10px">
                Este programa requiere una matrícula activa del estudiante para registrar las materias del pago.
              </Box>
            ) : null
          )}

          {/* Programas anualizados */}
          {showPickerSection && !isSemesterBased && (
            fetchingEnrollment ? (
              <Flex justifyContent="center" paddingY="lg">
                <Spinner />
              </Flex>
            ) : (
              <Month
                key={`month:${currentProgramType.type}:${currentEnrollment?.id ?? currentProgram.id}`}
                idStudent={studentId}
                type={currentProgramType.type}
                currentEnrollment={currentEnrollment || false}
                program={currentProgram}
                currentProgramType={currentProgramType}
                fetching={fetchingEnrollment}
                paymentDate={paymentDate}
                lockProgram={true}
                onStateChange={(st) => onChange({ monthState: st })}
                showRequiredLabel
              />
            )
          )}
        </Box>
      )}
    </Box>
  );
}
